import React, { useCallback, useContext, useRef, useMemo, useEffect, useState } from 'react';
import type { FC } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { defineMessages, useIntl } from 'react-intl-next';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { FlagsProvider } from '@atlaskit/flag';
import type { SVGProps } from '@atlaskit/icon/svg';

import { fg } from '@confluence/feature-gating';
import { keys as sessionStorageKeys } from '@confluence/storage-manager';
import {
	FEED_EXPERIENCE,
	ExperienceTrackerContext,
	ExperienceSuccess,
} from '@confluence/experience-tracker';
import {
	Attribution,
	GenericErrorBoundary,
	ErrorBoundary,
	ErrorDisplay,
	isExpectedApolloError,
} from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { ActivityFilterDialog } from '@confluence/activity-filter';
import { useBooleanFeatureFlag, useSessionData } from '@confluence/session-data';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics';

import { getInitialPageSize } from './initialPageSizeConstant';
import { FeedHeader } from './FeedHeader';
import { ActivityFeedCards, PopularFeedCards } from './FeedCards';
import { useFeedRenderedTrackEvent } from './useFeedRenderedTrackEvent';
import { useActivityFilterModalState } from './useActivityFilterModalState';
import {
	useFeedTypeSelector,
	useAnonymousFeedTypeSelector,
	useFeedTypeOverride,
	FeedType,
} from './useFeedTypeSelector';
import { GenericFeedError } from './FeedError';
import { FeedContentQuery } from './FeedContentQuery.graphql';
import { FeedPopularFeedQuery } from './FeedPopularFeedQuery.graphql';
import type {
	FeedContentQuery as FeedContentQueryType,
	FeedContentQueryVariables,
} from './__types__/FeedContentQuery';
import type {
	FeedPopularFeedQuery as FeedPopularFeedQueryType,
	FeedPopularFeedQueryVariables,
} from './__types__/FeedPopularFeedQuery';
import { SimpleWrapper } from './FeedCards/FeedCard/FeedCard';
import { HOME_OVERVIEW_FEED_PAGE_SEGMENT } from './perf.config';
import { FeedContext } from './FeedContext';
import { useFeedSession } from './useFeedSession';
import { FilterByLabelFeed } from './FilterByLabelFeed';
import { WelcomeFeedIcon } from './FeedTypeSelector/icons/WelcomeFeedIcon';
import { FarewellFeedIcon } from './FeedTypeSelector/icons/FarewellFeedIcon';

const i18n = defineMessages({
	welcomeFeedLabel: {
		id: 'feed.welcome-label',
		defaultMessage: 'Welcome',
		description: 'Label Text for the Welcome Feed',
	},
	farewellFeedLabel: {
		id: 'feed.farewell-label',
		defaultMessage: 'Farewell',
		description: 'Label Text for the Farewell Feed',
	},
});

export type CustomPanel = {
	title: string;
	key: string;
	icon?: (props: SVGProps) => JSX.Element;
	content: JSX.Element;
	ref?: React.RefObject<HTMLButtonElement>;
};

export enum PopularFeedTimeGranularityValue {
	Day = 'day',
	Default = 'default', // default is 2 weeks. cc-analytics expects "default" as a parameter for 2 weeks
	Month = 'month',
	Week = 'week',
	Year = 'year',
}

type FeedComponentProps = {
	renderPopularFeed: boolean;
	customPanels?: CustomPanel[];
};

const FeedComponent: FC<FeedComponentProps> = ({ renderPopularFeed, customPanels }) => {
	const { isLoggedIn } = useSessionData();
	const intl = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { feedTypeOverride, clearFeedTypeOverride } = useFeedTypeOverride();
	const loggedInFeedTypeSelector = useFeedTypeSelector();
	const anonymousFeedTypeSelector = useAnonymousFeedTypeSelector();
	const isSpaceAliasFFEnabled = useBooleanFeatureFlag('confluence.frontend.space.alias');
	const popularFeedPillRef = useRef<HTMLButtonElement | null>(null);
	const followingFeedPillRef = useRef<HTMLButtonElement | null>(null);
	const welcomeFeedPillRef = useRef<HTMLButtonElement | null>(null);
	const farewellFeedPillRef = useRef<HTMLButtonElement | null>(null);
	const [popularFeedTimeGranularity, setPopularFeedTimeGranularity] =
		useState<PopularFeedTimeGranularityValue>(PopularFeedTimeGranularityValue.Default);

	const handleFocusRef = (feedPillRef: React.RefObject<HTMLButtonElement>) => {
		const feedPillRefButton = feedPillRef.current;
		if (feedPillRefButton) {
			feedPillRefButton.focus();
		}
	};

	const {
		loading: loadingPreferences,
		queryError,
		mutationError,
		feedType,
		updateFeedType,
		hasSelectedFeedTypePreference,
	} = isLoggedIn ? loggedInFeedTypeSelector : anonymousFeedTypeSelector;

	const filterOrOverride = feedTypeOverride || feedType;

	const updateFeedTypeAndClearOverride = useCallback(
		(updatedFeedType: FeedType | string) => {
			clearFeedTypeOverride();
			updateFeedType(updatedFeedType);
		},
		[updateFeedType, clearFeedTypeOverride],
	);

	const handleFeedSelectorChange = useCallback(
		(updatedFeedType: FeedType | string) => {
			experienceTracker.start({
				name: FEED_EXPERIENCE,
				attributes: { feedTypeChange: true },
			});
			updateFeedTypeAndClearOverride(updatedFeedType);
		},
		[experienceTracker, updateFeedTypeAndClearOverride],
	);

	const feedContentQuery = useQuery<FeedContentQueryType, FeedContentQueryVariables>(
		FeedContentQuery,
		{
			variables: {
				first: getInitialPageSize(),
				includeAlias: isSpaceAliasFFEnabled,
			},
			errorPolicy: 'all',
			notifyOnNetworkStatusChange: true,
		},
	);
	if (feedContentQuery?.error) {
		if (isExpectedApolloError(feedContentQuery.error)) {
			markErrorAsHandled(feedContentQuery.error);
		}
	}

	const popularFeedQuery = useQuery<FeedPopularFeedQueryType, FeedPopularFeedQueryVariables>(
		FeedPopularFeedQuery,
		{
			variables: {
				first: getInitialPageSize(),
				includeAlias: isSpaceAliasFFEnabled,
				timeGranularity: popularFeedTimeGranularity,
			},
			context: { single: true },
			errorPolicy: 'all',
			notifyOnNetworkStatusChange: true,
			skip: !renderPopularFeed,
		},
	);

	const refetchPopularFeedQuery = popularFeedQuery.refetch;
	useEffect(() => {
		void refetchPopularFeedQuery?.({ timeGranularity: popularFeedTimeGranularity });
	}, [refetchPopularFeedQuery, popularFeedTimeGranularity]);

	useFeedRenderedTrackEvent(filterOrOverride, feedContentQuery.data, popularFeedQuery.data);

	const followFeedSessionId = useFeedSession(
		feedContentQuery,
		sessionStorageKeys.HOME_FOLLOWING_FEED_SESSION_ID,
	);
	const followingFeedContext = useMemo(() => {
		return { feedSessionId: followFeedSessionId };
	}, [followFeedSessionId]);
	const popularFeedSessionId = useFeedSession(
		popularFeedQuery,
		sessionStorageKeys.HOME_POPULAR_FEED_SESSION_ID,
	);
	const popularFeedContext = useMemo(() => {
		return { feedSessionId: popularFeedSessionId };
	}, [popularFeedSessionId]);

	const { activityFilterModalOpen, openActivityFilterModal, closeActivityFilterModal } =
		useActivityFilterModalState();

	const refetchFeedContentQuery = feedContentQuery.refetch;
	const handleActivityFilterModalSave = useCallback(() => {
		void refetchFeedContentQuery?.();
		handleFeedSelectorChange(FeedType.FOLLOWING);
	}, [refetchFeedContentQuery, handleFeedSelectorChange]);

	const welcomeAndFarewellPanels: CustomPanel[] = [
		{
			title: intl.formatMessage(i18n.welcomeFeedLabel),
			key: 'confluence.home.welcome',
			icon: WelcomeFeedIcon,
			content: (
				<FilterByLabelFeed
					handleFocusRef={() => handleFocusRef(welcomeFeedPillRef)}
					filterByLabel="welcome-blog"
				/>
			),
			ref: welcomeFeedPillRef,
		},
		{
			title: intl.formatMessage(i18n.farewellFeedLabel),
			key: 'confluence.home.farewell',
			icon: FarewellFeedIcon,
			content: (
				<FilterByLabelFeed
					handleFocusRef={() => handleFocusRef(farewellFeedPillRef)}
					filterByLabel="farewell-blog"
				/>
			),
			ref: farewellFeedPillRef,
		},
	];

	const updatedPanels = [
		...(fg('confluence_welcome_farewell_feed') ? welcomeAndFarewellPanels : []),
		...(customPanels || []),
	];

	const renderPanel = (panel: CustomPanel) => {
		return (
			<>
				<>{panel.content}</>
				<ExperienceSuccess //if we render an external panel we'll consider it a success, as the data is fetched externally
					name={FEED_EXPERIENCE}
					key={panel.key}
					attributes={{ feedType: panel.key }}
				/>
				<PageSegmentLoadEnd metric={HOME_OVERVIEW_FEED_PAGE_SEGMENT} />
			</>
		);
	};

	if (loadingPreferences) {
		return null;
	}

	const renderFeedCards = () => {
		if (
			updatedPanels &&
			!!updatedPanels.length &&
			//@ts-ignore
			!Object.values(FeedType).includes(filterOrOverride) // ensures the custom panel isn't just one of the expected panels
		) {
			const panel = updatedPanels.find((element) => element.key === filterOrOverride);

			const isPanelWelcomeOrFarewell =
				panel?.key === 'confluence.home.welcome' || panel?.key === 'confluence.home.farewell';

			if (isPanelWelcomeOrFarewell && panel?.content) {
				return renderPanel(panel);
			} else if (panel?.content) {
				return <SimpleWrapper>{renderPanel(panel)}</SimpleWrapper>;
			}
		}

		if (!hasSelectedFeedTypePreference) {
			if (feedContentQuery?.data?.feed?.nodes.length) {
				return (
					<FeedContext.Provider value={followingFeedContext}>
						<ActivityFeedCards
							feedContentQuery={feedContentQuery}
							openActivityFilterModal={openActivityFilterModal}
							handleFocusRef={() => handleFocusRef(followingFeedPillRef)}
						/>
					</FeedContext.Provider>
				);
			} else if (popularFeedQuery?.data?.popularFeed?.nodes.length) {
				return (
					<FeedContext.Provider value={popularFeedContext}>
						<PopularFeedCards
							popularFeedQuery={popularFeedQuery}
							handleFocusRef={() => handleFocusRef(popularFeedPillRef)}
							popularFeedTimeGranularity={popularFeedTimeGranularity}
						/>
					</FeedContext.Provider>
				);
			}
		}

		if (filterOrOverride === FeedType.POPULAR) {
			return (
				<FeedContext.Provider value={popularFeedContext}>
					<PopularFeedCards
						popularFeedQuery={popularFeedQuery}
						handleFocusRef={() => handleFocusRef(popularFeedPillRef)}
						popularFeedTimeGranularity={popularFeedTimeGranularity}
					/>
				</FeedContext.Provider>
			);
		}

		return (
			<FeedContext.Provider value={followingFeedContext}>
				<ActivityFeedCards
					feedContentQuery={feedContentQuery}
					openActivityFilterModal={openActivityFilterModal}
					handleFocusRef={() => handleFocusRef(followingFeedPillRef)}
				/>
			</FeedContext.Provider>
		);
	};

	return (
		<div data-testid="activity-feed">
			<FeedHeader
				openActivityFilterModal={openActivityFilterModal}
				renderPopularFeed={renderPopularFeed}
				feedType={filterOrOverride}
				setFeedType={handleFeedSelectorChange}
				customPanels={updatedPanels}
				popularFeedPillRef={popularFeedPillRef}
				followingFeedPillRef={followingFeedPillRef}
				popularFeedTimeGranularity={popularFeedTimeGranularity}
				setPopularFeedTimeGranularity={setPopularFeedTimeGranularity}
			/>
			<ErrorBoundary attribution={Attribution.SMARTS}>
				{renderFeedCards()}
				<ModalTransition>
					{activityFilterModalOpen && (
						<ActivityFilterDialog
							onChanged={handleActivityFilterModalSave}
							onClose={closeActivityFilterModal}
						/>
					)}
				</ModalTransition>
				{queryError && <ErrorDisplay error={queryError} />}
				{mutationError && <ErrorDisplay error={mutationError} />}
			</ErrorBoundary>
		</div>
	);
};

type FeedProps = {
	renderPopularFeed?: boolean;
	customPanels?: CustomPanel[];
};
export const Feed: FC<FeedProps> = ({ renderPopularFeed = false, customPanels }) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const failFeedExperience = useCallback(
		(error: Error) => {
			experienceTracker.fail({
				name: FEED_EXPERIENCE,
				error,
			});
		},
		[experienceTracker],
	);

	return (
		<FlagsProvider>
			<GenericErrorBoundary
				attribution={Attribution.SMARTS}
				renderOnError={() => <GenericFeedError />}
				onError={failFeedExperience}
			>
				<FeedComponent renderPopularFeed={renderPopularFeed} customPanels={customPanels} />
			</GenericErrorBoundary>
		</FlagsProvider>
	);
};
